import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { embedBrandLogo } from '@/utils/pdfLogo';
import { steps, type RequestRecord } from './model';

const BLUE = rgb(0.035, 0.18, 0.43);
const LIGHT_BLUE = rgb(0.93, 0.96, 0.99);
const TEXT = rgb(0.12, 0.16, 0.22);
const MUTED = rgb(0.38, 0.43, 0.5);
const BORDER = rgb(0.82, 0.86, 0.91);

function safeText(value: unknown) {
  return String(value ?? '').replace(/[–—]/g, '-');
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR');
}
function wrap(text: string, font: PDFFont, size: number, maxWidth: number) {
  const words = safeText(text).split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) line = next;
    else { if (line) lines.push(line); line = word; }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

export async function downloadRequestPdf(record: RequestRecord) {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedBrandLogo(pdf);
  const pageSize: [number, number] = [595.28, 841.89]; // A4 retrato
  const title = record.kind === 'projeto' ? 'PROJETO DE DESENVOLVIMENTO' : 'SOLICITACAO DE AMOSTRA';
  let page: PDFPage; let y: number;

  const addPage = () => {
    page = pdf.addPage(pageSize); y = 790;
    page.drawRectangle({ x: 0, y: 810, width: pageSize[0], height: 32, color: BLUE });
    page.drawText('ZSCHIMMER & SCHWARZ BRASIL', { x: 44, y: 822, size: 9, font: bold, color: rgb(1, 1, 1) });
    page.drawText(title, { x: 595.28 - 44 - bold.widthOfTextAtSize(title, 8), y: 822, size: 8, font: bold, color: rgb(1, 1, 1) });
    if (logo) page.drawImage(logo, { x: 44, y: 728, width: 72, height: 64 });
    page.drawText(safeText(record.protocol), { x: 132, y: 770, size: 16, font: bold, color: BLUE });
    page.drawText(safeText(record.status), { x: 132, y: 750, size: 10, font: bold, color: MUTED });
    y = 704;
  };
  addPage();

  page!.drawRectangle({ x: 44, y: y - 58, width: 507, height: 58, color: LIGHT_BLUE, borderColor: BORDER, borderWidth: 0.7 });
  page!.drawText('RESPONSAVEL', { x: 58, y: y - 20, size: 8, font: bold, color: BLUE });
  page!.drawText(safeText(record.owner), { x: 58, y: y - 39, size: 11, font: bold, color: TEXT });
  page!.drawText('DIVISAO', { x: 330, y: y - 20, size: 8, font: bold, color: BLUE });
  page!.drawText(safeText(record.division || '-'), { x: 330, y: y - 39, size: 11, font: bold, color: TEXT });
  y -= 78;
  page!.drawText(`Criado em: ${formatDate(record.createdAt)}`, { x: 44, y, size: 8.5, font: regular, color: MUTED });
  page!.drawText(`Atualizado em: ${formatDate(record.updatedAt)}`, { x: 300, y, size: 8.5, font: regular, color: MUTED });
  y -= 24;

  const ensureSpace = (height: number) => { if (y - height < 90) addPage(); };

  for (const step of steps[record.kind]) {
    ensureSpace(30);
    page!.drawRectangle({ x: 44, y: y - 20, width: 507, height: 20, color: BLUE });
    page!.drawText(safeText(step.title).toUpperCase(), { x: 50, y: y - 14.5, size: 9, font: bold, color: rgb(1, 1, 1) });
    y -= 20;

    for (const field of step.fields) {
      const value = record.data[field.key];
      if (!value) continue;
      const lines = wrap(value, regular, 8.5, 507 - 16);
      const height = Math.max(24, lines.length * 11 + 16);
      ensureSpace(height);
      page!.drawText(safeText(field.label).toUpperCase(), { x: 48, y: y - 12, size: 7, font: bold, color: MUTED });
      lines.forEach((line, index) => page!.drawText(line, { x: 48, y: y - 24 - index * 11, size: 8.5, font: regular, color: TEXT }));
      page!.drawLine({ start: { x: 44, y: y - height }, end: { x: 551, y: y - height }, thickness: 0.5, color: BORDER });
      y -= height;
    }
  }

  if (record.history.length) {
    ensureSpace(30);
    page!.drawRectangle({ x: 44, y: y - 20, width: 507, height: 20, color: BLUE });
    page!.drawText('HISTORICO DE STATUS', { x: 50, y: y - 14.5, size: 9, font: bold, color: rgb(1, 1, 1) });
    y -= 20;
    for (const entry of record.history) {
      const noteLines = wrap(entry.note, regular, 8, 507 - 16);
      const height = Math.max(24, noteLines.length * 10 + 16);
      ensureSpace(height);
      const headline = `${entry.from ? `${entry.from} -> ` : ''}${entry.to} - ${formatDate(entry.at)} (${safeText(entry.actor)})`;
      page!.drawText(headline, { x: 48, y: y - 12, size: 8, font: bold, color: TEXT });
      noteLines.forEach((line, index) => page!.drawText(line, { x: 48, y: y - 23 - index * 10, size: 7.5, font: regular, color: MUTED }));
      page!.drawLine({ start: { x: 44, y: y - height }, end: { x: 551, y: y - height }, thickness: 0.5, color: BORDER });
      y -= height;
    }
  }

  const pages = pdf.getPages();
  pages.forEach((current, index) => {
    current.drawLine({ start: { x: 44, y: 55 }, end: { x: 551, y: 55 }, thickness: 0.6, color: BORDER });
    current.drawText('Zschimmer & Schwarz do Brasil Ltda. | Documento confidencial', { x: 44, y: 38, size: 7, font: regular, color: MUTED });
    current.drawText(`Pagina ${index + 1} de ${pages.length}`, { x: 490, y: 38, size: 7, font: regular, color: MUTED });
  });

  const bytes = await pdf.save();
  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${record.protocol}.pdf`;
  anchor.click();
  URL.revokeObjectURL(url);
}
